import { RecognitionCategoryEditModal } from "@/pages/settings/recognitions/widgets/RecognitionCategoryEditModal.tsx";
import { RecognitionGiftDisabledTag } from "@/pages/settings/recognitions/widgets/RecognitionGiftDisabledTag.tsx";
import { useRecognitionCategoryDelete } from "@/queries/use-recognitions.query.ts";
import {
  ApiRecognitionCategory,
  getRecognitionGiftRuleGroups,
} from "@/types/recognitions/recognition.types.ts";
import {
  Card,
  ConfirmationModal,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Tag,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export interface Props {
  categories?: ApiRecognitionCategory[];
  isLoading: boolean;
}

export function RecognitionLibraryTab({ categories, isLoading }: Props) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const categoryDelete = useRecognitionCategoryDelete();

  return (
    <Stack className={"py-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"}>
      {isLoading ? (
        <Card isLoading={true} />
      ) : (
        <>
          {categories?.map((category) => (
            <Card
              key={category.id}
              header={category.title}
              caption={category.description}
              to={`/settings/recognitions/${category.id}`}
              buttonsSlot={
                <Stack direction={"horizontal"} gap={"xs"} items={"center"}>
                  <Tag>
                    {t("recognition:recognitions_count", {
                      count: category.recognitions_count,
                    })}
                  </Tag>
                  {!getRecognitionGiftRuleGroups(category).some(
                    (group) => group.rules.length > 0
                  ) && <RecognitionGiftDisabledTag />}
                </Stack>
              }
              menu={
                <>
                  <DropdownMenuItem
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      openModal(RecognitionCategoryEditModal, { categoryId: category.id });
                    }}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                    title={t("recognition:category_edit_title")}
                  />
                  {!category.is_system && (
                    <DropdownMenuItem
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        if (category.recognitions_count > 0) return;
                        e.preventDefault();
                        openModal(ConfirmationModal, {
                          question: t("recognition:category_delete_named", {
                            title: category.title,
                          }),
                          text: t("recognition:category_delete_confirm", {
                            title: category.title,
                          }),
                          acceptTitle: t("common:delete"),
                          onAccept: async () => {
                            await categoryDelete.mutateAsync(category.id);
                          },
                        });
                      }}
                      leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                      variant={category.recognitions_count > 0 ? "menu" : "menuDanger"}
                      disabled={category.recognitions_count > 0}
                      className={
                        category.recognitions_count > 0
                          ? "pointer-events-none opacity-70"
                          : undefined
                      }
                      title={
                        category.recognitions_count > 0
                          ? t("recognition:category_delete_unavailable")
                          : t("recognition:category_delete")
                      }
                      description={
                        category.recognitions_count > 0
                          ? t("recognition:category_delete_contains")
                          : undefined
                      }
                    />
                  )}
                </>
              }
            />
          ))}
          <Card
            icon={"fa-plus"}
            header={t("recognition:category_create_title")}
            caption={t("recognition:category_create_caption")}
            variant={"action"}
            onClick={() => openModal(RecognitionCategoryEditModal, {})}
          />
        </>
      )}
    </Stack>
  );
}
